<template>
  <div :class="[classes]">
    <div class="relative flex h-full gap-2 mt-6 justify-items-stretch">
      <Listbox as="div" class="w-[48%] h-full">
        <div class="relative flex flex-col justify-center w-full">
          <span class="text-xl font-medium whitespace-nowrap">
            {{ translatedAvailableItemsLabel }}
          </span>
          <hr v-show="!showSearch" class="mt-1 border-1 text-common-grey">
          <div v-show="showSearch" class="flex flex-row-reverse items-center gap-4 text-common-grey">
            <div class="relative w-full h-[50px] flex items-center flex-row-reverse">
              <input
                ref="leftListSearch"
                v-model="left_list_search_value"
                label=""
                :class="['opacity-100 w-full']"
                class="block w-full px-3 py-2 placeholder-gray-400 transition-all ease-in border border-gray-300 rounded-md shadow-sm appearance-none dark:bg-dark-box-background dark:text-white dark:border-dark-divider-border focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                name="name"
                autocomplete="off"
                :autofocus="true"
                tabindex="0"
                type="text"
                :placeholder="translatedPlaceholderItemsLabel">

              <div class="absolute flex flex-wrap justify-end top-1 right-1">
                <Listbox as="div" multiple class="flex-shrink-0">
                  <div class="relative">
                    <ListboxButton class="relative flex items-center w-full rounded-full whitespace-nowrap group">
                      <Funnel
                        class="flex-shrink-0 w-10 h-10 text-sm font-medium text-gray-500 rounded-full group-hover:bg-gray-100 dark:group-hover:bg-dark-box-background sm:px-3 sm:-ml-1"
                        aria-hidden="true" />
                      <!-- Blue dot for custom selection -->
                      <span
                        v-if="data_types.some((d) => d?.is_checked) && data_types.some((d) => !d?.is_checked)"
                        class="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full" />
                    </ListboxButton>

                    <ListboxOptions
                      class="absolute right-0 z-10 py-3 mt-0 overflow-auto text-base bg-white rounded-lg shadow-lg max-h-56 w-52 dark:bg-dark-box-background ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      <ListboxOption v-for="data_type in data_types" :key="data_type.value" as="template" :value="data_type">
                        <li
                          class="dark:bg-dark-box-background relative select-none py-2 px-3 cursor-pointer"
                          @click.stop="() => (data_type['is_checked'] = !data_type['is_checked'])">
                          <div class="flex items-center">
                            <span class="block text-sm font-medium truncate dark:text-white">
                              {{ data_type.label }}
                            </span>
                          </div>
                          <span v-if="data_type?.is_checked" class="absolute inset-y-0 right-0 flex items-center pr-3 text-blue">
                            <Check class="h-5 w-5" aria-hidden="true" />
                          </span>
                        </li>
                      </ListboxOption>
                    </ListboxOptions>
                  </div>
                </Listbox>
              </div>
            </div>
          </div>
          <div class="w-full" @keydown.ctrl.a="selectDeselectAllItemsHandler(left_list_items, is_left_list_all_selected)">
            <draggable
              id="left-list"
              v-model="left_list_items"
              as="ListboxOptions"
              static
              :class="[
                'bg-white  border-dark-divider-border dark:bg-dark-box-background',
                ` h-[55vh] overflow-scroll mt-1 border-0 ${
                  $store.state.ui.darkMode ? 'dark-scrollbar' : ''
                } dark:border-dark-divider-border rounded-lg text-base ring-0 overflow-auto focus:outline-none sm:text-sm transition ease-in-out duration-side-menu-transition-duration`
              ]"
              :component-data="{
                type: 'transition-group',
                name: !drag ? 'flip-list' : null
              }"
              v-bind="{
                sort: !disable_sort,
                animation: 200,
                group: 'group',
                ghostClass: 'ghost',
                chosenClass: 'chosen'
              }"
              :item-key="valueAttr"
              @start="startDragToRightList()"
              @end="endDragToRightList()">
              <template v-if="left_list_search_value && left_list_search_term_availble" #header>
                <div class="bg-white dark:bg-dark-box-background flex justify-center min-h-[500px] overflow-hidden rounded-lg p-5">
                  <div class="flex justify-center items-center flex-col">
                    <Info class="h-20 w-20 text-blue" aria-hidden="true" />
                    <h3 class="mt-2 text-xl font-normal text-black dark:text-white">
                      {{ $t("Metric with name") }} "{{ left_list_search_value }}" {{ $t("does not exist") }}
                    </h3>
                    <p class="mt-2 text-md font-normal text-gray-500">
                      {{ $t("You can create a new metric though") }}
                      <span class="text-blue font-semibold underline hover:cursor-pointer" @click="addNewMetric('left')">{{
                        $t("Create New Metric")
                      }}</span>
                    </p>
                  </div>
                </div>
              </template>
              <template #item="{ element }">
                <ListboxOption
                  :key="element.id"
                  class="py-2 pl-5 mb-1 bg-gray-100 cursor-move group hover:bg-blue hover:text-white hover:font-semibold"
                  as="template"
                  :value="getValue(element)">
                  <div class="flex items-center ml-1">
                    <input
                      :id="`left-list-item`"
                      :checked="element.selected"
                      type="checkbox"
                      class="w-5 h-5 border-gray-300 rounded focus:ring-blue-light text-blue-light group-hover:checked:bg-blue-dark"
                      @click.stop="handleSelectItem(element)">
                    <div class="flex flex-col justify-start pl-3">
                      <span class="text-md">
                        {{ getLabel(element) }}
                        <span
                          v-if="element?.data_sources?.length > 0"
                          v-tooltip="`Linked to ${element.data_sources.map((s) => s.origin.replace('ml.', '').title()).join(', ')}`"
                          class="text-sm">({{ element.data_sources.length }})</span>
                      </span>
                      <span class="text-sm font-light text-gray-900 group-hover:text-white"> {{ element.input_type }}</span>
                    </div>
                  </div>
                </ListboxOption>
              </template>
            </draggable>

            <div class="flex justify-center w-full mt-4 space-x-2">
              <button
                v-if="left_list_items.length"
                class="font-normal btn cancel-btn text-common-grey"
                @click="selectDeselectAllItemsHandler(left_list_items, is_left_list_all_selected)">
                {{ is_left_list_all_selected ? $t("Deselect") : $t("Select") }} {{ $t("all Items") }}
              </button>
            </div>
          </div>
        </div>
      </Listbox>

      <div class="w-[4%] flex flex-col justify-center items-center border-r-2 relative right-4 border-gray-100">
        <div class="relative flex flex-col items-center justify-center gap-1 left-4">
          <transition-group key="control" name="vertical-list">
            <div
              v-if="left_list_selected_count > 0"
              id="add-item"
              key="add-item"
              class="relative w-8 h-8 overflow-hidden font-medium text-center text-gray-500 bg-white rounded-full cursor-pointer dark:hover:opacity-80 focus:outline-none active:bg-gray-200"
              @click="moveSelectedToSecondList">
              <transition name="slide-left" duration="200">
                <CircleArrowRight
                  color="#27C884"
                  class="absolute inset-0 w-8 h-8 m-auto"
                  :class="[{ 'rotate-180': $store.state.portfolio.ui_options?.language == 'ar' }]" />
              </transition>
            </div>

            <div
              v-if="right_list_selected_count > 0"
              id="remove-item"
              key="remove-item"
              class="relative w-8 h-8 overflow-hidden font-medium text-center text-gray-500 bg-white rounded-full cursor-pointer dark:hover:opacity-80 focus:outline-none active:bg-gray-200"
              @click="moveSelectedToFirstList">
              <transition name="slide-right" duration="200">
                <CircleArrowRight
                  color="#F05B5B"
                  class="absolute inset-0 w-8 h-8 m-auto"
                  :class="[{ 'rotate-180': $store.state.portfolio.ui_options?.language == 'en' }]" />
              </transition>
            </div>
          </transition-group>
        </div>
      </div>

      <Listbox as="div" class="h-full w-[48%]">
        <div class="relative flex flex-col justify-center w-full">
          <span class="text-xl font-medium whitespace-nowrap">
            {{ translatedSelectedItemsLabel }}
          </span>
          <hr v-show="!showSearch" class="mt-1 border-1 text-common-grey">
          <div v-show="showSearch" class="flex flex-row-reverse items-center gap-4 text-common-grey">
            <div class="relative w-full h-[50px] flex items-center flex-row-reverse">
              <div class="absolute flex items-center justify-center w-5 h-full right-2">
                <transition name="fade" mode="out-in" duration="100">
                  <X
                    v-if="right_list_search_value"
                    color="#899399"
                    class="w-6 h-6 text-gray-400 cursor-pointer"
                    @click="
                      () => {
                        right_list_search_value = ''
                      }
                    " />
                </transition>
                <button
                  v-if="right_list_search_value && right_list_search_term_availble"
                  class="absolute w-24 h-8 btn primary-btn right-6"
                  @click="addNewMetric('right')">
                  {{ $t("Create New") }}
                </button>
              </div>

              <input
                ref="rightListSearch"
                v-model="right_list_search_value"
                label=""
                tabindex="0"
                autocomplete="off"
                :class="['opacity-100 w-full']"
                class="block w-full px-3 py-2 placeholder-gray-400 transition-all ease-in border border-gray-300 rounded-md shadow-sm appearance-none dark:bg-dark-box-background dark:text-white dark:border-dark-divider-border focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                name="name"
                type="text"
                :placeholder="translatedPlaceholderItemsLabel">
            </div>
          </div>
          <div class="w-full" @keydown.ctrl.a="selectDeselectAllItemsHandler(right_list_items, is_right_list_all_selected)">
            <draggable
              id="right-list"
              v-model="right_list_items"
              as="ListboxOptions"
              static
              :class="[
                'bg-white dark:border-dark-divider-border border-white dark:bg-dark-box-background',
                `list-group  h-[55vh] mt-1 overflow-scroll border-0 ${
                  $store.state.ui.darkMode ? 'dark-scrollbar' : ''
                } dark:border-dark-divider-border rounded-lg text-base overflow-auto focus:outline-none sm:text-sm transition ease-in-out duration-side-menu-transition-duration`
              ]"
              :component-data="{
                type: 'transition-group',
                name: !drag ? 'flip-list' : null
              }"
              v-bind="{
                type: 'transition-group',
                sort: !disable_sort,
                animation: 200,
                group: 'group',
                ghostClass: 'ghost',
                chosenClass: 'chosen'
              }"
              :item-key="valueAttr"
              @start="startDragToLeftList()"
              @end="endDragToLeftList()">
              <template #item="{ element }">
                <ListboxOption
                  :key="element.id"
                  class="py-2 pl-5 mb-1 bg-gray-100 cursor-move group hover:bg-blue hover:text-white hover:font-semibold"
                  as="template"
                  :value="getValue(element)">
                  <div class="flex items-center ml-1">
                    <input
                      :id="`right-list-item`"
                      :checked="element.selected"
                      type="checkbox"
                      class="w-5 h-5 border-gray-300 rounded focus:ring-blue-light text-blue-light group-hover:checked:bg-blue-dark"
                      @click.stop="handleSelectItem(element)">
                    <div class="flex flex-col justify-start pl-3">
                      <span class="text-md">
                        {{ getLabel(element) }}
                      </span>
                      <span class="text-sm font-light group-hover:text-white"> {{ element.input_type }}</span>
                    </div>
                  </div>
                </ListboxOption>
              </template>
            </draggable>

            <div class="flex justify-center w-full mt-4 space-x-2">
              <button
                v-if="right_list_items.length"
                class="font-normal btn cancel-btn text-common-grey"
                @click="selectDeselectAllItemsHandler(right_list_items, is_right_list_all_selected)">
                {{ is_right_list_all_selected ? $t("Deselect") : $t("Select") }} {{ $t("all Items") }}
              </button>
            </div>
          </div>
        </div>
      </Listbox>
    </div>

    <p
      class="first-letter:capitalize absolute right-0 leading-tight pt-[1px] text-[12px] text-red-400"
      :class="[errorMessage || meta.valid ? 'visible' : 'invisible']">
      {{ errorMessage || successMessage }}
    </p>
  </div>
</template>

<script>
import draggable from "vuedraggable"
import { useField } from "vee-validate"
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/vue"
import { utils } from "@/store/helpers"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { CircleArrowRight, Funnel, Check, Info, X } from "lucide-vue-next"

export default {
  components: {
    Funnel,
    Check,
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    Info,
    draggable,
    X,
    CircleArrowRight
  },

  inject: ["setPageLayout", "updateLayouts"],
  props: {
    objectType: {
      type: String,
      required: false,
      default: ""
    },
    viewType: {
      type: String,
      required: false,
      default: ""
    },
    allItems: {
      type: Array,
      required: true
    },
    classes: {
      type: String,
      default: ""
    },
    selectedItems: {
      type: Array,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    availableItemsLabel: {
      type: String,
      default: "Available Metrics"
    },
    selectedItemsLabel: {
      type: String,
      default: "Included Metrics"
    },
    placeholderItemsLabel: {
      type: String,
      default: "Search or create metric"
    },
    labelAttr: {
      type: String,
      required: true,
      validator(v) {
        return v !== undefined
      }
    },
    valueAttr: {
      type: String,
      required: true,
      validator(v) {
        return v !== undefined
      }
    },
    successMessage: {
      type: String,
      default: ""
    },
    returnObjects: {
      type: Boolean,
      default: false
    },
    showFilters: {
      type: Boolean,
      default: false
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    isActivitiesHeader: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:currentItems"],
  setup(props) {
    const { errorMessage, meta } = useField(props.name, undefined, {
      initialValue: props.selectedItems ? props.selectedItems : []
    })
    return {
      errorMessage,
      meta
    }
  },
  data() {
    return {
      data_types: this.$consts.DATA_TYPES.concat([
        {
          label: this.$t("Standard Metrics"),
          value: "show_standard_metrics"
        }
      ]).map((d) => ({
        ...d,
        is_checked: this.$store.state?.ui_options.metrics_search_selected_data_types?.includes(d.value)
      })),
      show_standard_metrics: false,
      list_selected: "",
      left_list_search_value: "",
      right_list_search_value: "",
      drag: false,
      selected_filters: [],
      left_list_items: [],
      left_list_items_original: [],
      right_list_items_original: [],
      right_list_items: [],
      tmp_list: [],
      disable_sort: false,
      dont_trigger_left_list_items_watch: false,
      dont_trigger_right_list_items_watch: false
    }
  },
  computed: {
    translatedAvailableItemsLabel() {
      return this.$t(this.availableItemsLabel)
    },
    translatedSelectedItemsLabel() {
      return this.$t(this.selectedItemsLabel)
    },
    translatedPlaceholderItemsLabel() {
      return this.$t(this.placeholderItemsLabel)
    },
    left_list_search_term_availble() {
      if (this.left_list_items.length) {
        return false
      }
      return true
    },
    right_list_search_term_availble() {
      if (this.right_list_items.length) {
        return false
      }
      return true
    },
    left_list_selected_count() {
      let selected_count = 0
      this.left_list_items.forEach((item) => {
        if (item.selected) {
          selected_count += 1
        }
      })
      return selected_count
    },
    right_list_selected_count() {
      let selected_count = 0
      this.right_list_items.forEach((item) => {
        if (item.selected) {
          selected_count += 1
        }
      })
      return selected_count
    },

    is_left_list_all_selected() {
      let all_selected = true

      this.left_list_items.forEach((item) => {
        if (!item.selected) {
          all_selected = false
        }
      })
      return all_selected
    },
    is_right_list_all_selected() {
      let all_selected = true

      this.right_list_items.forEach((item) => {
        if (!item.selected) {
          all_selected = false
        }
      })
      return all_selected
    }
  },

  watch: {
    selected_filters() {
      this.left_list_items = this.left_list_items_original
        .map((i) => {
          return { ...i, selected: false }
        })
        .filter(
          (m) =>
            (this.showFilters && this.selected_filters.length > 0 && this.selected_filters.includes(m.data_type)) ||
            this.selected_filters.length == 0
        )
      this.right_list_items = this.right_list_items_original
        .map((i) => {
          return { ...i, selected: false }
        })
        .filter(
          (m) =>
            (this.showFilters && this.selected_filters.length > 0 && this.selected_filters.includes(m.data_type)) ||
            this.selected_filters.length == 0
        )
    },
    right_list_items: {
      deep: true,
      handler() {
        this.updateState()
      }
    },
    data_types: {
      deep: true,
      handler() {
        if (!this.isActivitiesHeader) {
          const left_list_calc = []
          this.left_list_items_original.forEach((item) => {
            if (
              (item.name.toLowerCase().includes(this.left_list_search_value.toLowerCase()) &&
                this.data_types.find((d) => d.value == item.data_type)?.is_checked) ||
              (item.name.toLowerCase().includes(this.left_list_search_value.toLowerCase()) &&
                this.data_types.filter((d) => d.is_checked).length == 0) ||
              (this.data_types.find((d) => d.value == "show_standard_metrics")?.is_checked && item.is_standard_rollup_metric)
            ) {
              left_list_calc.push(item)
            }
          })

          this.left_list_items = left_list_calc

          this.$store.dispatch("ui/setUIValue", {
            metrics_search_selected_data_types: this.data_types.filter((x) => x.is_checked).map((o) => o?.value)
          })
        }
      }
    },
    left_list_search_value: {
      deep: true,
      handler() {
        const left_list_calc = []
        this.left_list_items_original.forEach((item) => {
          if (
            (item.name.toLowerCase().includes(this.left_list_search_value.toLowerCase()) &&
              this.data_types.find((d) => d.value == item.data_type)?.is_checked) ||
            (item.name.toLowerCase().includes(this.left_list_search_value.toLowerCase()) &&
              this.data_types.filter((d) => d.is_checked).length == 0) ||
            (this.data_types.find((d) => d.value == "show_standard_metrics")?.is_checked && item.is_standard_rollup_metric)
          ) {
            left_list_calc.push(item)
          }
        })

        this.left_list_items = left_list_calc
      }
    },

    right_list_search_value() {
      const right_list_calc = []
      this.right_list_items_original.forEach((item) => {
        if (item.name.toLowerCase().includes(this.right_list_search_value.toLowerCase())) {
          right_list_calc.push(item)
        }
      })
      this.right_list_items = right_list_calc
    }
  },

  mounted() {
    this.left_list_items = this.allItems
      .filter((x) => x)
      .filter((item) => !this.selectedItems.find((sitem) => (sitem.id ? item.id === sitem.id : item.id === sitem)))
    this.left_list_items.map((item) => {
      item["selected"] = false
    })
    this.left_list_items_original = [...this.left_list_items]
    this.left_list_items = this.left_list_items.filter((m) => !m.is_standard_rollup_metric)
    this.right_list_items = utils.clone(this.selectedItems)
    this.right_list_items.map((item) => {
      item["selected"] = false
    })
    this.right_list_items_original = [...this.right_list_items]
    this.data_types = this.$consts.DATA_TYPES.concat([
      {
        label: this.$t("Standard Metrics"),
        value: "show_standard_metrics"
      }
    ]).map((d) => ({
      ...d,
      is_checked: this.$store.state?.ui_options?.metrics_search_selected_data_types?.includes(d.value)
    }))
  },
  created() {
    this.$emitter?.on(ALL_EMITTERS.CUSTOM_METRIC_ADDED_IN_METRICS, (metric) => {
      if (this.list_selected == "left") {
        this.left_list_items.unshift(metric)
      } else {
        this.right_list_items.unshift(metric)
      }
    })
  },
  deactivated() {
    this.$emitter.off(ALL_EMITTERS.CUSTOM_METRIC_ADDED_IN_METRICS)
  },

  methods: {
    getLabel(item) {
      if (typeof item == "object" && this.labelAttr) {
        return item[this.labelAttr]
      }
      return item
    },
    addNewMetric(list) {
      this.list_selected = list
      ;(this.left_list_search_value = ""),
        (this.right_list_search_value = ""),
        this.$emitter.emit(ALL_EMITTERS.CREATE_CUSTOM_METRIC, {
          object_type: this.objectType,
          view_type: this.viewType
        })
    },

    startDragToRightList() {
      this.drag = true
      this.tmp_list = utils.clone(this.right_list_items)
    },
    endDragToRightList() {
      this.disable_sort = false
      this.drag = false
      let index
      this.right_list_items.map((item) => {
        index = this.tmp_list.findIndex((x) => x.reference_name == item.reference_name)
        if (index == -1) {
          this.right_list_items_original.push({ ...item, selected: false })
          const ind = this.left_list_items_original.findIndex((i) => i.id == item.id)
          this.left_list_items_original.splice(ind, 1)
        }
      })
      if (this.left_list_items.length == 0) {
        this.left_list_search_value = ""
      }
    },
    startDragToLeftList() {
      this.drag = true
      this.tmp_list = utils.clone(this.left_list_items)
    },
    endDragToLeftList() {
      this.drag = false
      let index
      this.left_list_items.map((item) => {
        index = this.tmp_list.findIndex((x) => x.reference_name == item.reference_name)
        if (index == -1) {
          this.left_list_items_original.push({ ...item, selected: false })
          const ind = this.right_list_items_original.findIndex((i) => i.id == item.id)
          this.right_list_items_original.splice(ind, 1)
        }
      })
      if (this.right_list_items.length == 0) {
        this.right_list_search_value = ""
      }
    },
    selectDeselectAllItemsHandler(items_list, is_selected_all) {
      this.dont_trigger_left_list_items_watch = true
      this.dont_trigger_right_list_items_watch = true

      // in case of every thing is selected then deselect all
      if (is_selected_all) {
        items_list?.forEach((item) => {
          item["selected"] = false
        })
      } else {
        items_list?.forEach((item) => {
          item["selected"] = true
        })
      }

      setTimeout(() => {
        this.dont_trigger_left_list_items_watch = false
        this.dont_trigger_right_list_items_watch = false
      }, 200)
    },

    moveSelectedToSecondList() {
      const left_list_items_calc = []

      this.left_list_items.forEach((item) => {
        if (item.selected) {
          this.right_list_items.push({ ...item, selected: false })
          const index = this.left_list_items_original.findIndex((i) => i.id == item.id)
          this.left_list_items_original.splice(index, 1)
          this.right_list_items_original.push({ ...item, selected: false })
        } else {
          left_list_items_calc.push(item)
        }
      })

      this.left_list_items = left_list_items_calc
    },

    moveSelectedToFirstList() {
      const right_list_items_calc = []
      this.right_list_items.forEach((item) => {
        if (item.selected) {
          this.left_list_items.push({ ...item, selected: false })
          const index = this.right_list_items_original.findIndex((i) => i.id == item.id)
          this.right_list_items_original.splice(index, 1)
          this.left_list_items_original.push({ ...item, selected: false })
        } else {
          right_list_items_calc.push(item)
        }
      })

      this.right_list_items = right_list_items_calc
    },

    handleSelectItem(item) {
      this.dont_trigger_left_list_items_watch = true
      this.dont_trigger_right_list_items_watch = true
      item.selected = !item.selected
      setTimeout(() => {
        this.dont_trigger_left_list_items_watch = false
        this.dont_trigger_right_list_items_watch = false
      }, 200)
    },

    updateState() {
      if (this.returnObjects) {
        this.$emit("update:currentItems", this.right_list_items)
      } else {
        this.$emit(
          "update:currentItems",
          this.right_list_items.map((x) => this.getValue(x))
        )
      }
    },

    getValue(item) {
      if (typeof item == "object" && this.valueAttr) {
        return item[this.valueAttr]
      }
      return item
    }
  }
}
</script>
<style scoped>
.ghost {
  @apply opacity-50 bg-white border-0;
}
.chosen {
  @apply opacity-50 bg-white border-0;
}
.flip-list {
  transition: all 0.25s ease-out;
}
</style>
